/**
 * Wellness API Service
 *
 * Handles wellness (B2C participant) subscription management
 * Uses wellnessApiClient which handles token expiration and refresh automatically
 */

import axios from 'axios';
import { wellnessApiClient } from './wellnessApiClient';
import type {
  SubscriptionStatusResponse,
  SubscriptionCheckoutRequest,
  SubscriptionCheckoutResponse,
  SubscriptionPortalRequest,
  SubscriptionPortalResponse,
  WellnessSubscription,
  WellnessUser,
  GetProductsResponse,
  StripeProduct,
  StripeCoupon,
  GetParticipantsResponse,
  GetWorkshopsResponse,
  GetWorkshopResponse,
  WorkshopRegistrationResponse,
  WorkshopJoinResponse,
  AddParticipantRequest,
  AddParticipantResponse,
  SurveyInfo,
  LinkParticipantResponse,
} from '@/types/wellness';

/**
 * Fetch current subscription status and usage
 * Requires wellness authentication (handled automatically by interceptor)
 * Returns null if no subscription found (404)
 */
export const fetchSubscriptionStatus = async (): Promise<WellnessSubscription | null> => {
  try {
    const response = await wellnessApiClient.get<SubscriptionStatusResponse>('/self/subscription/status');
    return response.data.subscription;
  } catch (error) {
    // 404 means no subscription found - this is a valid state
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    // Re-throw other errors
    throw error;
  }
};

/**
 * Create a Stripe checkout session or update subscription
 * Requires wellness authentication (handled automatically by interceptor)
 * 
 * Returns different response types based on flow:
 * - New subscription: returns checkoutUrl (redirect to Stripe Checkout)
 * - Plan switch/add-on with payment required: returns checkoutUrl (redirect to Stripe Portal)
 * - Plan switch/add-on with payment succeeded: returns message (no redirect, show success)
 */
export const createCheckoutSession = async (
  request: SubscriptionCheckoutRequest
): Promise<SubscriptionCheckoutResponse> => {
  const response = await wellnessApiClient.post<SubscriptionCheckoutResponse>(
    '/self/subscription/checkout',
    request
  );

  return response.data;
};

/**
 * Get Stripe billing portal URL for subscription management
 * Requires wellness authentication (handled automatically by interceptor)
 */
export const getPortalUrl = async (
  request: SubscriptionPortalRequest
): Promise<string> => {
  const response = await wellnessApiClient.post<SubscriptionPortalResponse>(
    '/self/subscription/portal',
    request
  );

  return response.data.portalUrl;
};

/**
 * Fetch available subscription products/plans and coupons
 * Requires wellness authentication (handled automatically by interceptor)
 */
export const fetchProducts = async (): Promise<{ products: StripeProduct[]; coupons: StripeCoupon[] }> => {
  const response = await wellnessApiClient.get<GetProductsResponse>('/self/subscription/getproducts');
  return {
    products: response.data.data,
    coupons: response.data.coupons || [],
  };
};

/**
 * Get all participants for the authenticated user
 * Requires wellness authentication (handled automatically by interceptor)
 * Returns participants grouped by person
 */
export const getParticipants = async (): Promise<GetParticipantsResponse> => {
  const response = await wellnessApiClient.get<GetParticipantsResponse>('/self/participants');
  return response.data;
};

/**
 * Get upcoming workshops for the authenticated user
 * Requires wellness authentication (handled automatically by interceptor)
 */
export const getWorkshops = async (): Promise<GetWorkshopsResponse> => {
  const response = await wellnessApiClient.get<GetWorkshopsResponse>('/self/workshops');
  return response.data;
};

/**
 * Get a single workshop by Google Calendar event ID
 */
export const getWorkshop = async (eventId: string): Promise<GetWorkshopResponse> => {
  const response = await wellnessApiClient.get<GetWorkshopResponse>(
    `/self/workshops/${encodeURIComponent(eventId)}`
  );
  return response.data;
};

/**
 * Register the authenticated user for a workshop
 */
export const registerForWorkshop = async (
  eventId: string
): Promise<WorkshopRegistrationResponse> => {
  const response = await wellnessApiClient.post<WorkshopRegistrationResponse>(
    `/self/workshops/${encodeURIComponent(eventId)}/registration`
  );
  return response.data;
};

/**
 * Cancel the authenticated user's workshop registration
 */
export const cancelWorkshopRegistration = async (
  eventId: string
): Promise<WorkshopRegistrationResponse> => {
  const response = await wellnessApiClient.delete<WorkshopRegistrationResponse>(
    `/self/workshops/${encodeURIComponent(eventId)}/registration`
  );
  return response.data;
};

/**
 * Get the approved join URL for a registered workshop
 */
export const getWorkshopJoinUrl = async (
  eventId: string
): Promise<WorkshopJoinResponse> => {
  const response = await wellnessApiClient.get<WorkshopJoinResponse>(
    `/self/workshops/${encodeURIComponent(eventId)}/join`
  );
  return response.data;
};

/**
 * Add a participant to a self-registration survey
 * Requires wellness authentication (handled automatically by interceptor)
 */
export const addParticipant = async (
  request: AddParticipantRequest
): Promise<AddParticipantResponse> => {
  const response = await wellnessApiClient.post<AddParticipantResponse>(
    '/self/participant',
    request
  );

  return response.data;
};

/**
 * Delete a participant record
 * Requires wellness authentication (handled automatically by interceptor)
 * Note: This endpoint may not exist - check API documentation
 */
export const deleteParticipant = async (
  surveyParticipantId: string
): Promise<void> => {
  await wellnessApiClient.delete(`/self/participant/${surveyParticipantId}`);
};

/**
 * Get survey information by survey ID
 * Requires wellness authentication (handled automatically by interceptor)
 */
export const getSurveyInfo = async (surveyId: string): Promise<SurveyInfo> => {
  const response = await wellnessApiClient.get<SurveyInfo>(`/self/survey/${surveyId}`);
  return response.data;
};

/**
 * Link an authenticated user's account to a participant record using a parent survey authentication code
 * Requires wellness authentication (handled automatically by interceptor)
 */
export const linkParticipant = async (
  parentSurveyAuthCode: string
): Promise<LinkParticipantResponse> => {
  const response = await wellnessApiClient.post<LinkParticipantResponse>(
    '/self/participant/link',
    {
      parentSurveyAuthCode,
    }
  );
  return response.data;
};

/**
 * Activate B2C status for user before checkout
 * Sets custom:b2c attribute in Cognito to true
 * Returns updated user object
 * Requires wellness authentication (handled automatically by interceptor)
 */
export const activateB2C = async (): Promise<{ user: WellnessUser }> => {
  const response = await wellnessApiClient.get<{ user: WellnessUser }>(
    '/self/subscription/activate-b2c'
  );
  return response.data;
};

/**
 * Get PDF report URL for a participant
 * Requires wellness authentication (handled automatically by interceptor)
 * Note: Only GET endpoint is available - participants cannot generate reports on-demand
 * 
 * @param surveyParticipantId - The participant ID
 * @param parentAuthCode - The parent survey authentication code (from parentSurveyAuthCode)
 * @param appliedTo - Optional survey target audience (e.g., "adult") - used for template selection
 * @returns Signed URL for the PDF report
 * @throws Error if report is not available (404) or other errors occur
 */
export const getParticipantReportUrl = async (
  surveyParticipantId: string,
  parentAuthCode: string,
  appliedTo?: string,
  disposition: 'inline' | 'attachment' = 'inline'
): Promise<string> => {
  // Use 'adult' template if appliedTo is 'adult', otherwise use 'parent'
  const template = appliedTo === 'adult' ? 'adult' : 'parent';
  const url = `/reports/v2/${template}/${surveyParticipantId}?parentAuthCode=${encodeURIComponent(parentAuthCode)}&disposition=${disposition}`;
  
  try {
    const response = await wellnessApiClient.get<{
      statusCode?: number;
      body?: {
        url: string;
      };
      url?: string; // Handle case where response might be directly { url: "..." }
    }>(url);
    
    // Handle different possible response structures
    const pdfUrl = response.data.body?.url || response.data.url;
    if (!pdfUrl) {
      throw new Error('Invalid response format from PDF report service.');
    }
    
    return pdfUrl;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error('PDF report is not available. Please contact support if you need assistance.');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized to access this report.');
      }
    }
    throw error;
  }
};